import { Button, CheckBox, LoadingSpinner, TextBox } from '@idexx-laboratories/spot-react';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react';
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import FieldGroupInCell from '../../components/FieldGroupInCell/FieldGroupInCell';
import Grid from '../../components/Grid/Grid';
import Loader from '../../components/Loader/Loader';
import SearchBarExtended from '../../components/SearchBarExtended/SearchBarExtended';
import { useStore } from '../../hooks/use-store';
import { useCheckSinglePermission } from '../../security/security-hook';
import { PERMISSION_UPDATE, RESOURCE_GLOBAL_VALUES } from '../../types/users-types';
import './UpgradeManagement.scss';

const UpgradeManagement = observer(() => {
  const { upgradeManagementStore: store } = useStore();
  const {
    loaders,
    itemsFiltered,
    colDefs,
    operationInProgress,
    maxUpgrades,
    maxUpgradesEditMode,
    maxUpgradesCurrent,
    maxUpgradesError,
    searchText,
    futureOnly,
    sorting,
    editItemIndex,
  } = store;

  const navigate = useNavigate();

  useEffect(() => void store.init(), [store]);

  const editMaxUpdatesAllowed = useCheckSinglePermission(RESOURCE_GLOBAL_VALUES, PERMISSION_UPDATE);

  if (loaders.page) return <LoadingSpinner size='large' />;

  return (
    <div className='upgrade-management_wrapper'>
      <div className='spot-typography__heading--level-2'>Upgrade Management</div>
      <div className='spot-typography__text--tertiary'>Search and manage Cornerstone upgrades.</div>
      <div className='page-top-actions first'>
        <div>
          <SearchBarExtended
            id='upgrade-search-bar'
            aria-label='Label Search'
            value={searchText}
            placeholder='Search SAP ID, Practice Name, Practice Contacts, Last Updated By'
            disabled={loaders.refresh || loaders.maxUpgrades || operationInProgress}
            onSearchClick={v => runInAction(() => (store.searchText = v))}
          />
          <CheckBox
            id='show-future-upgrades-only'
            label='Show Future Upgrades Only'
            aria-label='Show Future Upgrades Only'
            className='show-future-upgrades-only'
            checked={futureOnly}
            disabled={loaders.refresh || loaders.maxUpgrades || operationInProgress}
            onChange={() => runInAction(() => (store.futureOnly = !futureOnly))}
          />
          <span className='max-upgrades_wrapper'>
            Maximum upgrades per day
            <FieldGroupInCell
              htmlFor='max-upgrades-textbox'
              errorText={maxUpgradesError}
              style={{ display: 'inline-block' }}
            >
              <TextBox
                id='max-upgrades-textbox'
                className='text-box'
                data-testid='max-upgrades-textbox'
                value={maxUpgradesEditMode ? maxUpgradesCurrent : maxUpgrades}
                disabled={!maxUpgradesEditMode}
                onChange={e => store.changeMaxUpgrades(e.target.value)}
              />
            </FieldGroupInCell>
            {editMaxUpdatesAllowed &&
              (!maxUpgradesEditMode ? (
                <Button
                  variant='link'
                  title='Edit'
                  style={{ paddingLeft: '10px' }}
                  iconLeft='edit'
                  onClick={() => store.startEditingMaxUpgrades()}
                />
              ) : (
                <>
                  <Button
                    variant='link'
                    title='Save'
                    style={{ paddingLeft: '10px' }}
                    iconLeft={loaders.maxUpgrades ? undefined : 'checkmark'}
                    onClick={() => void store.updateMaxUpgrades()}
                    disabled={!maxUpgradesCurrent}
                  >
                    <Loader loading={loaders.maxUpgrades} />
                  </Button>
                  <Button
                    variant='link'
                    title='Cancel'
                    iconLeft='cancel'
                    disabled={loaders.maxUpgrades}
                    onClick={() => store.cancelEditingMaxUpgrades()}
                  />
                </>
              ))}
          </span>
          {loaders.refresh && <LoadingSpinner />}
        </div>
        <Button
          variant='primary'
          size='small'
          disabled={loaders.refresh || operationInProgress}
          onClick={() => void store.refresh()}
        >
          Refresh List
        </Button>
      </div>
      <div className='page-top-actions'>
        <div>
          <Button
            aria-label='Bulk Version Change'
            variant='primary'
            size='small'
            disabled={loaders.refresh || loaders.save || editItemIndex > -1}
            onClick={() => store.updateVersionsModal()}
          >
            Bulk Version Change
          </Button>
          <Button
            aria-label='Add Item'
            variant='link'
            size='small'
            iconLeft='plus'
            disabled={loaders.refresh || editItemIndex > -1}
            onClick={() => store.addItem()}
          >
            New Record
          </Button>
        </div>
      </div>
      <Grid id='upgrade-management-grid' columns={colDefs} data={itemsFiltered} sorting={sorting} />
      <div className='horizontal-flex-box page-bottom-actions'>
        <Button className='back' variant='link' size='small' onClick={() => navigate(-1)}>
          Back
        </Button>
        <div className='main-buttons'>
          <Button variant='primary' size='small' onClick={() => navigate('/')}>
            Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
});

export default UpgradeManagement;
